import React, {useMemo} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildCountyPath,
    buildCountryPath,
    buildHotspotDetailPath,
    getCountryLabel,
    getHotspotExplorerHeroImage,
    getHotspotExplorerStats,
    getStateProvinceLabel,
    getVisibleHotspots,
    sortHotspots
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";

export function StateHotspotsView({hotspotExplorerIndex}: HotspotExplorerProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {countryCode = '', stateProvince = ''} = useParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const stateKey = `${countryCode}|${stateProvince}`;
    const hotspots = useMemo(() => hotspotExplorerIndex.hotspotsByStateKey.get(stateKey) ?? [], [hotspotExplorerIndex, stateKey]);
    const regionBounds = hotspots[0]?.regionData?.subnational1?.bounds ?? undefined;
    const stateLabel = hotspots[0]?.stateLabel || getStateProvinceLabel(stateProvince);
    const visibleHotspots = useMemo(
        () => sortHotspots(getVisibleHotspots(hotspots, {countryCode, stateProvince}), sortMode),
        [countryCode, hotspots, sortMode, stateProvince]
    );
    const countyGroups = hotspotExplorerIndex.countyGroupsByStateKey.get(stateKey) ?? [];

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    }

    const openCounty = (county: string) => {
        navigate(`${buildCountyPath(countryCode, stateProvince, county)}${location.search}`);
    }

    const openHotspot = (locationId: string) => {
        navigate(buildHotspotDetailPath(locationId), {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    }

    return (
        <HotspotExplorerShell
            backgroundImage={getHotspotExplorerHeroImage({routeLevel: 'state', regionBounds})}
            eyebrow="Hotspot Explorer"
            title={stateLabel}
            intro={`Hotspots you've visited in ${stateLabel}, ${getCountryLabel(countryCode)}.`}
            backLabel={`Back to ${getCountryLabel(countryCode)}`}
            onBack={() => navigate(`${buildCountryPath(countryCode)}${location.search}`)}
            stats={getHotspotExplorerStats(visibleHotspots, 'Counties')}
            childStatLabel="Counties Visited"
            panelHeading="By County"
            groups={countyGroups}
            activeGroupCode={undefined}
            onSelectGroup={openCounty}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            onOpenHotspot={openHotspot}
        />
    );
}
